import { useState, useRef } from 'react';
import { X, Upload, Globe, Link, AlertTriangle, Loader2, Info } from 'lucide-react';
import {
  useImportAgentSkill,
  useImportFromUrl,
} from '../../../../api/agent-skills';
import { ScopeSelector, type SkillScope } from './ScopeSelector';
import { getApiErrorMessage } from '../../../../lib/utils';

import { useTranslation, Trans } from 'react-i18next';

interface ImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImported: (skillId: string) => void;
  isAdmin?: boolean;
}

type ImportTab = 'file' | 'url';

/** Detect whether an API error message is a name collision. */
function isNameConflict(msg: string): boolean {
  return /already exists/i.test(msg);
}

export function ImportDialog({
  isOpen,
  onClose,
  onImported,
  isAdmin = false,
}: ImportDialogProps) {
  const { t } = useTranslation('pleri/skills');
  const [activeTab, setActiveTab] = useState<ImportTab>('file');
  const [url, setUrl] = useState('');
  const [nameOverride, setNameOverride] = useState('');
  const [scope, setScope] = useState<SkillScope>('user');
  const [error, setError] = useState<string | null>(null);
  const [isConflict, setIsConflict] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const [nameChanged, setNameChanged] = useState<{ original: string; assigned: string } | null>(null);
  const fileRef = useRef<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importSkill = useImportAgentSkill();
  const importFromUrl = useImportFromUrl();

  if (!isOpen) return null;

  const resetState = () => {
    setError(null);
    setIsConflict(false);
    setWarning(null);
    setNameChanged(null);
  };

  const handleResult = (result: {
    data?: { scope: string; name: string };
    warning?: string;
    nameChanged?: boolean;
    originalName?: string;
    assignedName?: string;
  }) => {
    if (result.warning) {
      setWarning(result.warning);
    }
    if (result.nameChanged && result.originalName && result.assignedName) {
      setNameChanged({ original: result.originalName, assigned: result.assignedName });
    }
    if (result.data) {
      onImported(`${result.data.scope}:${result.data.name}`);
      // Don't close if there's a warning — let the user read it, then close
      if (!result.warning && !result.nameChanged) {
        onClose();
      }
    }
  };

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    fileRef.current = file;
    resetState();
    try {
      const result = await importSkill.mutateAsync({
        zipFile: file,
        nameOverride: nameOverride.trim() || undefined,
        scope,
      });
      handleResult(result);
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Import failed');
      setError(msg);
      if (isNameConflict(msg)) {
        setIsConflict(true);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRetryWithName = async () => {
    if (!nameOverride.trim() || !fileRef.current) return;
    resetState();
    try {
      const result = await importSkill.mutateAsync({
        zipFile: fileRef.current,
        nameOverride: nameOverride.trim(),
        scope,
      });
      handleResult(result);
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Import failed');
      setError(msg);
      if (isNameConflict(msg)) setIsConflict(true);
    }
  };

  const handleUrlImport = async () => {
    if (!url.trim()) return;
    resetState();
    try {
      const result = await importFromUrl.mutateAsync({
        url: url.trim(),
        nameOverride: nameOverride.trim() || undefined,
        scope,
      });
      handleResult(result);
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Failed to import from URL');
      setError(msg);
      if (isNameConflict(msg)) setIsConflict(true);
    }
  };

  const isPending = importSkill.isPending || importFromUrl.isPending;

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={t('importSkill')}
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl dark:bg-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-orange-500" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t('importSkillTitle')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          {(['file', 'url'] as ImportTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); resetState(); }}
              className={`flex items-center gap-1.5 px-5 py-2.5 text-xs font-medium transition-colors ${
                activeTab === tab
                  ? 'border-b-2 border-orange-500 text-orange-700 dark:text-orange-300'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab === 'file' ? <Upload className="h-3.5 w-3.5" /> : <Globe className="h-3.5 w-3.5" />}
              {tab === 'file' ? t('uploadFile') : t('fromUrl')}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="space-y-4 p-5">
          {activeTab === 'file' ? (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">
                {t('uploadAZipOrSkillFileFollowing')}
              </p>
              <div className="flex items-center justify-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".zip,.skill"
                  onChange={handleFileImport}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isPending}
                  className="flex items-center gap-2 rounded-md border-2 border-dashed border-gray-300 px-8 py-6 text-sm text-gray-500 transition-all duration-200 hover:border-orange-400 hover:text-orange-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:hover:border-orange-700 dark:hover:text-orange-400"
                >
                  {importSkill.isPending
                    ? <Loader2 className="h-5 w-5 animate-spin" />
                    : <Upload className="h-5 w-5" />}
                  {importSkill.isPending ? t('importing') : t('chooseFileToImport')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">URL</label>
                <div className="relative">
                  <Link className="absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  <input
                    className="w-full rounded-md border border-gray-300 py-2 pr-3 pl-9 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    placeholder="https://github.com/org/repo or https://example.com/skill.zip"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleUrlImport(); }}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {t('pasteAGitHubRepositoryUrlOrA')}
                </p>
              </div>
            </div>
          )}

          {/* Scope selector — reuses the same component as the create form */}
          <ScopeSelector value={scope} onChange={setScope} isAdmin={isAdmin} />

          {/* Name override — shown always (for proactive use) or highlighted on conflict */}
          <div>
            <label className={`mb-1 block text-xs font-medium ${isConflict ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`}>
              {isConflict ? t('skillNameConflictChooseADifferentName') : t('skillNameOverrideOptional')}
            </label>
            <input
              className={`w-full rounded-md border py-1.5 px-3 text-sm dark:bg-gray-700 dark:text-gray-100 ${
                isConflict
                  ? 'border-orange-400 bg-orange-50 dark:border-orange-600 dark:bg-orange-950/20'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              placeholder={t('eGMyThreatScanner')}
              value={nameOverride}
              onChange={(e) => setNameOverride(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            />
            <p className="mt-0.5 text-xs text-gray-400">
              {t('overridesTheNameInSkillMdLowercase')}
            </p>
          </div>

          {/* Retry button on conflict */}
          {isConflict && activeTab === 'file' && nameOverride.trim() && (
            <button
              onClick={handleRetryWithName}
              disabled={isPending}
              className="w-full rounded-md bg-orange-600 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700 disabled:opacity-50"
            >
              {isPending ? (
                <Loader2 className="mx-auto h-4 w-4 animate-spin" />
              ) : (
                t('importAs', { nameOverride })
              )}
            </button>
          )}

          {/* Import button (URL tab) */}
          {activeTab === 'url' && (
            <div className="flex justify-end">
              <button
                onClick={handleUrlImport}
                disabled={isPending || !url.trim()}
                className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {importFromUrl.isPending
                  ? <span className="flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" />{t('importing')}</span>
                  : t('import')}
              </button>
            </div>
          )}

          {/* Name changed notice */}
          {nameChanged && (
            <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm dark:border-blue-800 dark:bg-blue-950/40">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
              <span className="text-blue-700 dark:text-blue-300">
                <Trans
                  t={t}
                  i18nKey="importedAsSkillMdSpecifiedWhichWas"
                  values={{
                    assigned: nameChanged.assigned,
                    original: nameChanged.original,
                  }}
                  components={{
                    strong: <strong />,
                    span: <span className="font-mono text-xs" />,
                    button: (
                      <button onClick={onClose} className="ml-2 underline" />
                    ),
                  }}
                />
              </span>
            </div>
          )}

          {/* Shadow warning */}
          {warning && !nameChanged && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm dark:border-amber-800 dark:bg-amber-950/40">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
              <span className="text-amber-700 dark:text-amber-300">
                {warning}
                <button onClick={onClose} className="ml-2 underline">
                  {t('okGotIt')}
                </button>
              </span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>
                {isConflict
                  ? <>{t('aSkillWithThisNameAlreadyExists')}</>
                  : error}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
